/**
 * @file: feed.service.ts
 * @copyright: (c) 2019-2021 sichuan zhichetech co., ltd.
 *
 * Workshop-app feed inbox client. Talks to gateway `/api/feed/*` — the
 * gateway derives the inbox identity from the authenticated session, so
 * the client never sends platformUserId/scope.
 */

import { HttpClient } from '@euler/lib/request';
import {
  FeedInboxItem,
  FeedInboxListResult,
  FeedMessageType,
  FeedThreadListResult,
  FeedThreadScope,
  FeedUnreadCounts,
} from '@euler/model/feed';

export interface ListFeedMessagesOptions {
  type?: FeedMessageType | FeedMessageType[];
  authorId?: string;
  unread?: boolean;
  q?: string;
  cursor?: string;
  limit?: number;
}

export interface ListFeedThreadsOptions {
  unread?: boolean;
  /** Restrict to one or more thread scopes. */
  scope?: FeedThreadScope | FeedThreadScope[];
  /** Drop threads whose scope is in this list — complement of `scope`. */
  excludeScopes?: FeedThreadScope[];
  includeArchived?: boolean;
  /** Return only archived threads (the archived view). */
  archivedOnly?: boolean;
  cursor?: string;
  limit?: number;
}

export class FeedService {
  constructor(private readonly api: HttpClient) {}

  // ---------------- messages ----------------

  async listMessages(
    options: ListFeedMessagesOptions = {},
  ): Promise<FeedInboxListResult> {
    return await this.api
      .get()
      .url('/feed/messages', {
        type: serializeType(options.type),
        includeMarketingFollowup: 1,
        authorId: options.authorId,
        unread: options.unread ? 1 : undefined,
        q: options.q,
        cursor: options.cursor,
        limit: options.limit,
      })
      .future();
  }

  async getMessage(id: string): Promise<FeedInboxItem> {
    return await this.api.get().url('/feed/messages/:id', { id }).future();
  }

  async getUnreadCount(): Promise<FeedUnreadCounts> {
    return await this.api
      .get()
      .url('/feed/unread-count', { includeMarketingFollowup: 1 })
      .future();
  }

  async markMessageRead(id: string): Promise<void> {
    await this.api.post().url('/feed/messages/:id/read', { id }).future();
  }

  async markAllMessagesRead(
    options: {
      type?: FeedMessageType | FeedMessageType[];
      excludeType?: FeedMessageType | FeedMessageType[];
      authorId?: string;
    } = {},
  ): Promise<void> {
    await this.api
      .post()
      .url('/feed/messages/read-all')
      .data({
        type: serializeType(options.type),
        excludeType: serializeType(options.excludeType),
        authorId: options.authorId,
      })
      .future();
  }

  /**
   * Mark the caller's maintenance-plan reminder messages read for a set of
   * plan details — used after converting those details into a lead so the
   * whole bundle clears from the inbox at once.
   */
  async markRemindersReadByPlanDetails(planDetailIds: number[]): Promise<void> {
    await this.api
      .post()
      .url('/feed/messages/read-by-plan-details')
      .data({ planDetailIds })
      .future();
  }

  async archiveMessage(id: string): Promise<void> {
    await this.api.post().url('/feed/messages/:id/archive', { id }).future();
  }

  // ---------------- threads ----------------

  async listThreads(
    options: ListFeedThreadsOptions = {},
  ): Promise<FeedThreadListResult> {
    return await this.api
      .get()
      .url('/feed/threads', {
        unread: options.unread ? 1 : undefined,
        scope: serializeScopes(options.scope),
        excludeScopes: serializeScopes(options.excludeScopes),
        includeArchived: options.includeArchived ? 1 : undefined,
        archivedOnly: options.archivedOnly ? 1 : undefined,
        includeMarketingFollowup: 1,
        cursor: options.cursor,
        limit: options.limit,
      })
      .future();
  }

  async listMessagesInThread(
    threadId: string,
    options: {
      cursor?: string;
      limit?: number;
      includeArchived?: boolean;
    } = {},
  ): Promise<FeedInboxListResult> {
    return await this.api
      .get()
      .url('/feed/threads/:threadId/messages', {
        threadId,
        cursor: options.cursor,
        limit: options.limit,
        includeArchived: options.includeArchived ? 1 : undefined,
      })
      .future();
  }

  async markThreadRead(threadId: string): Promise<void> {
    await this.api
      .post()
      .url('/feed/threads/:threadId/read', { threadId })
      .future();
  }

  async archiveThread(threadId: string, on = true): Promise<void> {
    await this.api
      .post()
      .url('/feed/threads/:threadId/archive', { threadId })
      .data({ on })
      .future();
  }

  /**
   * Hard view-side delete — distinct from `archiveThread`. Wipes the
   * caller's copy of the thread; a later feed event recreates it empty.
   */
  async deleteThread(threadId: string): Promise<void> {
    await this.api
      .delete()
      .url('/feed/threads/:threadId', { threadId })
      .future();
  }

  async muteThread(threadId: string, on = true): Promise<void> {
    await this.api
      .post()
      .url('/feed/threads/:threadId/mute', { threadId })
      .data({ on })
      .future();
  }

  async pinThread(threadId: string, on = true): Promise<void> {
    await this.api
      .post()
      .url('/feed/threads/:threadId/pin', { threadId })
      .data({ on })
      .future();
  }
}

function serializeType(
  type: FeedMessageType | FeedMessageType[] | undefined,
): string | undefined {
  if (type == null) return undefined;
  return Array.isArray(type) ? type.join(',') : type;
}

function serializeScopes(
  scope: FeedThreadScope | FeedThreadScope[] | undefined,
): string | undefined {
  if (scope == null) return undefined;
  if (Array.isArray(scope)) {
    return scope.length === 0 ? undefined : scope.join(',');
  }
  return scope;
}
